import React, {
  useEffect,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import ToolTip from 'core/components/Tooltip';
import Popover from 'core/components/Popover';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import {
  Button,
} from 'pages/Dashboard/components/Button';
import {
  formatTwoDigitDate,
} from 'utils/date';
import getProviderSeenDatePairError from 'pages/Dashboard/components/ReferringProvider/providerContextDates';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';

type Props = {
  text?: string;
  dateSeen?: {
    firstSeenOn?: string | null;
    lastSeenOn?: string | null;
    onChange?: (value: {
      firstSeenOn?: string | null;
      lastSeenOn?: string | null;
    }) => void;
  };
  className?: string;
  alignment?: 'center' | 'left' | 'right';
};

export default function CompactDateEditor({
  text = 'Referring Provider',
  dateSeen,
  className,
  alignment = 'center',
}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftFirstSeenOn, setDraftFirstSeenOn] = useState<string | null>(
    dateSeen?.firstSeenOn ?? null,
  );
  const [draftLastSeenOn, setDraftLastSeenOn] = useState<string | null>(
    dateSeen?.lastSeenOn ?? null,
  );

  useEffect(() => {
    if (!isNil(anchorEl)) {
      setDraftFirstSeenOn(dateSeen?.firstSeenOn ?? null);
      setDraftLastSeenOn(dateSeen?.lastSeenOn ?? null);
    }
  }, [anchorEl, dateSeen?.firstSeenOn, dateSeen?.lastSeenOn]);

  const handleOpen = (element: HTMLElement) => {
    setAnchorEl(element);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const datePairError = getProviderSeenDatePairError(draftFirstSeenOn, draftLastSeenOn);

  const handleSave = () => {
    if (!isNil(datePairError)) {
      return;
    }

    dateSeen?.onChange?.({
      firstSeenOn: draftFirstSeenOn ?? null,
      lastSeenOn: draftLastSeenOn ?? null,
    });
    handleClose();
  };

  return (
    <>
      <ToolTip title={`Click to change ${text.toLowerCase()} dates`}>
        <div
          className={clsx(
            'flex w-fit cursor-pointer items-center rounded border border-gray-300 p-1 font-normal hover:bg-white hover:shadow hover:shadow-gray-200',
            alignment === 'left' ? 'ml-0' : 'ml-auto',
            className,
          )}
          role="button"
          tabIndex={0}
          aria-label={`Change ${text.toLowerCase()} dates`}
          onClick={(event) => { handleOpen(event.currentTarget); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleOpen(event.currentTarget);
            }
          }}
        >
          <div className={clsx('flex items-center gap-2 text-xs leading-none text-gray-500', {
            'justify-center text-center': alignment === 'center',
            'justify-start text-left': alignment === 'left',
            'justify-end text-right': alignment === 'right',
          })}
          >
            <span className="whitespace-nowrap">
              <span className="text-gray-600">First seen:</span>
              {' '}
              <span className="text-gray-600 font-semibold">{dateSeen?.firstSeenOn ? formatTwoDigitDate(dateSeen.firstSeenOn) : DEFAULT_EMPTY_VALUE}</span>
            </span>
            <span aria-hidden="true">•</span>
            <span className="whitespace-nowrap">
              <span className="text-gray-600">Last seen:</span>
              {' '}
              <span className="text-gray-600 font-semibold">{dateSeen?.lastSeenOn ? formatTwoDigitDate(dateSeen.lastSeenOn) : DEFAULT_EMPTY_VALUE}</span>
            </span>
          </div>
        </div>
      </ToolTip>

      <Popover
        anchorEl={anchorEl}
        onClose={handleClose}
        classes={{ paper: '!p-4 !rounded !shadow-2xl !border !border-gray-200' }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div className="flex w-[20rem] flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-gray-500">
              First seen
            </span>
            <DatePicker
              value={draftFirstSeenOn ?? ''}
              onChange={(value) => { setDraftFirstSeenOn(value?.format('YYYY-MM-DD') ?? null); }}
              showDateButtons={false}
              className="!rounded"
              containerClassName="!w-full"
              inputContainerClassName="!w-full"
              disableFuture
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-gray-500">
              Last seen
            </span>
            <DatePicker
              value={draftLastSeenOn ?? ''}
              onChange={(value) => { setDraftLastSeenOn(value?.format('YYYY-MM-DD') ?? null); }}
              showDateButtons={false}
              className="!rounded"
              containerClassName="!w-full"
              inputContainerClassName="!w-full"
              disableFuture
            />
          </div>
          {!isNil(datePairError) && (
            <p className="text-xs font-semibold text-red-600">
              {datePairError}
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outlined"
              color="primary"
              className="!h-8 !px-3"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              className="!h-8 !px-3"
              disabled={!isNil(datePairError)}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
